import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import {
  MessageSquare, Send, Mic, MicOff, Square, Play, Pause,
  User, Search, Loader2, Volume2, FileText, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { SpeechToTextButton } from '../components/SpeechToTextButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Messaging() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribeEnabled, setTranscribeEnabled] = useState(true);
  const [playingAudio, setPlayingAudio] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const audioPlayerRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    fetchContacts();
  }, []);

  useEffect(() => {
    if (selectedPartner) fetchMessages(selectedPartner.id);
  }, [selectedPartner]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/messages/conversations`, { withCredentials: true });
      setConversations(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const fetchContacts = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/by-org`, { withCredentials: true });
      setContacts(data.filter(c => c.id !== user?.id));
    } catch (err) { console.error(err); }
  };

  const fetchMessages = async (partnerId) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/messages?partner_id=${partnerId}`, { withCredentials: true });
      setMessages(data.reverse());
    } catch (err) { console.error(err); }
  };

  const sendTextMessage = async () => {
    if (!textInput.trim() || !selectedPartner) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/messages`, {
        recipient_id: selectedPartner.id,
        content: textInput,
        message_type: 'text'
      }, { withCredentials: true });
      setTextInput('');
      fetchMessages(selectedPartner.id);
      fetchConversations();
    } catch (err) { toast.error('Failed to send message'); }
    setSending(false);
  };

  // Voice Recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch (err) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    clearInterval(timerRef.current);
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setRecordingTime(0);
  };

  const sendVoiceMessage = async () => {
    if (!audioBlob || !selectedPartner) return;
    setSending(true);
    try {
      const form = new FormData();
      form.append('recipient_id', selectedPartner.id);
      form.append('audio', audioBlob, 'voice.webm');
      form.append('transcribe', transcribeEnabled ? 'true' : 'false');
      await axios.post(`${API_URL}/api/messages/voice`, form, { withCredentials: true });
      setAudioBlob(null);
      setRecordingTime(0);
      fetchMessages(selectedPartner.id);
      fetchConversations();
      toast.success(transcribeEnabled ? 'Voice message sent with transcription' : 'Voice message sent');
    } catch (err) { toast.error('Failed to send voice message'); }
    setSending(false);
  };

  const playAudio = (audioData, msgId) => {
    if (playingAudio === msgId) {
      audioPlayerRef.current?.pause();
      setPlayingAudio(null);
      return;
    }
    const audio = new Audio(audioData);
    audio.onended = () => setPlayingAudio(null);
    audioPlayerRef.current = audio;
    audio.play();
    setPlayingAudio(msgId);
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const filteredContacts = contacts.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectContact = (contact) => {
    setSelectedPartner({ id: contact.partner_id || contact.id, name: contact.partner_name || contact.name, photo: contact.profile_photo_url });
  };

  return (
    <DashboardLayout>
      <div className="flex h-[calc(100vh-8rem)] bg-muted rounded-xl border border-border overflow-hidden" data-testid="messaging-page">
        {/* Sidebar - Conversations */}
        <div className={`w-full sm:w-80 border-r border-border flex flex-col bg-card ${selectedPartner ? 'hidden sm:flex' : 'flex'}`}>
          <div className="p-4 border-b border-border">
            <h2 className="text-lg font-heading font-bold text-foreground mb-3 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#00A6A0]" />Messages
            </h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search contacts..."
                className="bg-muted border-border text-foreground pl-10 h-9 text-sm"
                data-testid="search-contacts-input" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {/* Existing conversations */}
            {conversations.length > 0 && (
              <div className="p-2">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-2 mb-1">Recent</p>
                {conversations.map(c => (
                  <button key={c.partner_id} onClick={() => selectContact(c)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${selectedPartner?.id === c.partner_id ? 'bg-[#00A6A0]/10' : 'hover:bg-muted'}`}
                    data-testid={`conversation-${c.partner_id}`}>
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-[#00A6A0]/20 text-[#00A6A0] text-xs">{getInitials(c.partner_name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground font-medium truncate">{c.partner_name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{c.last_message}</p>
                    </div>
                    {c.unread_count > 0 && (
                      <span className="w-5 h-5 rounded-full bg-[#00A6A0] text-[#0F1419] text-[10px] font-bold flex items-center justify-center">{c.unread_count}</span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* All contacts */}
            <div className="p-2">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider px-2 mb-1">Contacts</p>
              {filteredContacts.map(c => (
                <button key={c.id} onClick={() => selectContact(c)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${selectedPartner?.id === c.id ? 'bg-[#00A6A0]/10' : 'hover:bg-muted'}`}
                  data-testid={`contact-${c.id}`}>
                  <Avatar className="h-9 w-9">
                    {c.profile_photo_url ? (
                      <AvatarImage src={c.profile_photo_url} alt={c.name} />
                    ) : (
                      <AvatarFallback className="bg-[#FF6F61]/20 text-[#FF6F61] text-xs">{getInitials(c.name)}</AvatarFallback>
                    )}
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground font-medium truncate">{c.name}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{c.role?.replace('_', ' ')}</p>
                  </div>
                </button>
              ))}
              {filteredContacts.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No contacts found</p>}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className={`flex-1 flex flex-col ${!selectedPartner ? 'hidden sm:flex' : 'flex'}`}>
          {selectedPartner ? (
            <>
              {/* Chat Header */}
              <div className="flex items-center gap-3 p-4 border-b border-border bg-card">
                <button onClick={() => setSelectedPartner(null)} className="sm:hidden p-1 rounded hover:bg-muted">
                  <ChevronLeft className="w-5 h-5 text-muted-foreground" />
                </button>
                <Avatar className="h-9 w-9">
                  {selectedPartner.photo ? (
                    <AvatarImage src={selectedPartner.photo} alt={selectedPartner.name} />
                  ) : (
                    <AvatarFallback className="bg-[#00A6A0]/20 text-[#00A6A0] text-xs">{getInitials(selectedPartner.name)}</AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <p className="text-sm font-medium text-foreground">{selectedPartner.name}</p>
                  <p className="text-[10px] text-muted-foreground">Online</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.map(msg => {
                  const isMine = msg.sender_id === user?.id;
                  return (
                    <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[70%] rounded-2xl p-3 ${isMine ? 'bg-[#00A6A0] text-[#0F1419]' : 'bg-card text-foreground border border-border'}`}
                        data-testid={`message-${msg.id}`}>
                        {msg.message_type === 'voice' && msg.audio_data && (
                          <button onClick={() => playAudio(msg.audio_data, msg.id)}
                            className={`flex items-center gap-2 mb-1.5 text-sm ${isMine ? 'text-[#0F1419]/80' : 'text-[#00A6A0]'}`}
                            data-testid={`play-voice-${msg.id}`}>
                            {playingAudio === msg.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                            <Volume2 className="w-3.5 h-3.5" />
                            <span className="text-xs">Voice Message</span>
                          </button>
                        )}
                        {msg.message_type === 'voice' && msg.transcript && (
                          <div className={`text-[10px] mb-1 flex items-center gap-1 ${isMine ? 'text-[#0F1419]/60' : 'text-muted-foreground'}`}>
                            <FileText className="w-3 h-3" />Transcript
                          </div>
                        )}
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${isMine ? 'text-[#0F1419]/50' : 'text-muted-foreground'}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Voice Recording Preview */}
              {audioBlob && !isRecording && (
                <div className="px-4 py-2 border-t border-border bg-card flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2">
                    <Mic className="w-4 h-4 text-[#00A6A0]" />
                    <span className="text-sm text-foreground">Voice message ({formatTime(recordingTime)})</span>
                    <label className="flex items-center gap-1.5 ml-auto cursor-pointer">
                      <input type="checkbox" checked={transcribeEnabled} onChange={e => setTranscribeEnabled(e.target.checked)}
                        className="rounded border-border bg-muted" data-testid="transcribe-toggle" />
                      <span className="text-[10px] text-muted-foreground">Transcribe</span>
                    </label>
                  </div>
                  <Button size="sm" variant="ghost" className="text-red-400" onClick={cancelRecording} data-testid="cancel-voice-btn">Cancel</Button>
                  <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419]" onClick={sendVoiceMessage} disabled={sending}
                    data-testid="send-voice-btn">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-border bg-card">
                {isRecording ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-sm text-red-400 font-mono">{formatTime(recordingTime)}</span>
                      <span className="text-xs text-muted-foreground">Recording...</span>
                    </div>
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={cancelRecording} data-testid="cancel-recording-btn">
                      <MicOff className="w-4 h-4" />
                    </Button>
                    <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" onClick={stopRecording} data-testid="stop-recording-btn">
                      <Square className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost"
                      className="text-muted-foreground hover:text-[#00A6A0] p-2"
                      onClick={startRecording} data-testid="start-recording-btn">
                      <Mic className="w-5 h-5" />
                    </Button>
                    <SpeechToTextButton compact
                      onTranscript={text => setTextInput(prev => prev ? prev + ' ' + text : text)}
                    />
                    <Input value={textInput} onChange={e => setTextInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendTextMessage()}
                      placeholder="Type a message..."
                      className="flex-1 bg-muted border-border text-foreground text-sm"
                      data-testid="message-text-input" />
                    <Button size="sm" className="bg-[#00A6A0] hover:bg-[#008A85] text-[#0F1419] p-2"
                      onClick={sendTextMessage} disabled={!textInput.trim() || sending}
                      data-testid="send-text-btn">
                      {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <MessageSquare className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-heading text-foreground mb-2">Select a conversation</h3>
              <p className="text-sm text-muted-foreground">Choose a contact to start messaging</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
